import { createSelector } from '@reduxjs/toolkit';
import { useMemo } from 'react';

import { useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store';
import { removeDuplicates } from 'src/utils/array';
import { isUnreadNotification } from 'src/utils/NotificationsUtils';

const selectFileNotificationData = createSelector(
  (state: RootState) => state.notifications.fileNotifications,
  (state: RootState) => state.activeCompany.activeCompanyId,
  (state: RootState) => state.notifications.esigRequestCompleteNotifications,
  (fileNotifications, activeCompanyId, esigRequestCompleteNotifications) => ({
    fileNotifications,
    activeCompanyId,
    esigRequestCompleteNotifications,
  }),
);

export const useFilesNotificationCount = (): number => {
  const {
    fileNotifications,
    activeCompanyId,
    esigRequestCompleteNotifications,
  } = useAppSelector(selectFileNotificationData);

  /**
   * Calculates for each notification group its total unread notification,
   * since files and e-sign unread notifications are calculated differently.
   * Files unread notifications count is the sum of file-upload notified channels.
   * E-sign notifications count is the sum of esign-requests/signature complete
   * actions received at channel.
   */
  return useMemo(() => {
    const unreadFileUploadNotifications = fileNotifications.filter(
      (notif) =>
        isUnreadNotification(notif.ownerId) &&
        notif.fields.companyId === activeCompanyId,
    );
    const fileUploadNotifiedChannelsIds = removeDuplicates(
      unreadFileUploadNotifications.map((notif) => notif.fields.channelId),
    );

    const unreadEsigRequestCompleteNotifications =
      esigRequestCompleteNotifications.filter((notif) =>
        isUnreadNotification(notif.ownerId),
      );

    return (
      fileUploadNotifiedChannelsIds.length +
      unreadEsigRequestCompleteNotifications.length
    );
  }, [fileNotifications, esigRequestCompleteNotifications, activeCompanyId]);
};
